import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { EmployeeUser } from '../types/employeeUser';

const COLLECTION_NAME = 'calendars-users';

const retryOperation = async <T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 400));
    }
  }
  throw new Error('Max retries exceeded');
};

const toDate = (value: unknown): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value);
  }

  return new Date();
};

const mapUser = (id: string, data: Record<string, unknown>): EmployeeUser => ({
  id,
  userId: String(data.userId ?? ''),
  name: String(data.name ?? ''),
  avatar: String(data.avatar ?? ''),
  idByOA: String(data.idByOA ?? ''),
  followedOA: Boolean(data.followedOA),
  isSensitive: Boolean(data.isSensitive),
  isAdmin: Boolean(data.isAdmin),
  dataEntryRole: Boolean(data.dataEntryRole),
  employmentType: data.employmentType === 'fulltime' || data.employmentType === 'parttime'
    ? data.employmentType
    : undefined,
  defaultPosition:
    data.defaultPosition === 'table' || data.defaultPosition === 'bar' || data.defaultPosition === 'security'
      ? data.defaultPosition
      : undefined,
  hourlyWage: typeof data.hourlyWage === 'number' ? data.hourlyWage : 0,
  createdAt: toDate(data.createdAt),
  updatedAt: toDate(data.updatedAt),
  updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
});

const sortByNewest = (a: EmployeeUser, b: EmployeeUser): number => {
  return b.updatedAt.getTime() - a.updatedAt.getTime();
};

export const employeeUserService = {
  async addUser(user: Omit<EmployeeUser, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    return retryOperation(async () => {
      const now = new Date();
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...user,
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    });
  },

  async getUserByUserId(userId: string): Promise<EmployeeUser | null> {
    return retryOperation(async () => {
      const userQuery = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      const snapshot = await getDocs(userQuery);
      if (snapshot.empty) {
        return null;
      }

      const users = snapshot.docs.map((item) => mapUser(item.id, item.data() as Record<string, unknown>));
      users.sort(sortByNewest);
      return users[0];
    });
  },

  async getAllUsers(): Promise<EmployeeUser[]> {
    return retryOperation(async () => {
      const usersQuery = query(collection(db, COLLECTION_NAME), orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(usersQuery);
      return snapshot.docs
        .map((item) => mapUser(item.id, item.data() as Record<string, unknown>))
        .sort(sortByNewest);
    });
  },

  async getAllUsersDeduplicated(): Promise<EmployeeUser[]> {
    const users = await this.getAllUsers();
    const dedupedMap = new Map<string, EmployeeUser>();

    users.forEach((user) => {
      const existingUser = dedupedMap.get(user.userId);
      if (!existingUser || user.updatedAt.getTime() > existingUser.updatedAt.getTime()) {
        dedupedMap.set(user.userId, user);
      }
    });

    return Array.from(dedupedMap.values()).sort(sortByNewest);
  },

  async updateUserByUserId(userId: string, payload: Partial<EmployeeUser>, updatedBy?: string): Promise<void> {
    return retryOperation(async () => {
      const targetUser = await this.getUserByUserId(userId);
      if (!targetUser?.id) {
        throw new Error('Không tìm thấy nhân viên để cập nhật');
      }

      await updateDoc(doc(db, COLLECTION_NAME, targetUser.id), {
        ...payload,
        updatedAt: new Date(),
        updatedBy: updatedBy ?? payload.updatedBy ?? 'system',
      });
    });
  },

  async deleteUserByUserId(userId: string): Promise<void> {
    return retryOperation(async () => {
      const userQuery = query(collection(db, COLLECTION_NAME), where('userId', '==', userId));
      const snapshot = await getDocs(userQuery);

      await Promise.all(snapshot.docs.map((item) => deleteDoc(item.ref)));
    });
  },
};
